import { IS_ARTIST } from '@disco/constants/FeatureTypes'

import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { loadBusinessUsers } from '@disco/state-management/actions/BusinessUsersActions'
import { loadBusinessSubscription } from '@disco/state-management/actions/UserActions'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import {
  getSubscriptionPeriod,
  getSelfServeBillingPermissions,
} from '@disco/state-management/selectors/subscription'
import {
  getBusinessUsers,
  getCorePlan,
  getTracksLimits,
  getUser,
  getUsersLimits,
} from '@disco/state-management/selectors/user'
import SubscribedUserBilling from './SubscribedUserBilling'

/**
 * SubscribedUserBillingContainer component for managing billing data and state
 *
 * A container component that fetches and manages billing-related data from Redux state,
 * including subscription information, user limits, and business users. Handles loading
 * states and passes processed data to the SubscribedUserBilling presentation component.
 *
 * @returns JSX element containing the billing container with data management
 */
const SubscribedUserBillingContainer = (): React.ReactNode => {
  const dispatch = useDispatch()
  const user = useSelector(getUser)
  const subscription = useSelector(state => state.subscription)
  const businessUsers = useSelector(getBusinessUsers)
  const usersLimits = useSelector(getUsersLimits)
  const tracksLimits = useSelector(getTracksLimits)
  const billingProviderComponents = useSelector(
    state => state.billingProviderComponents,
  )
  const corePlan = useSelector(getCorePlan)
  const billingProviderSeatsComponentsPresent =
    Boolean(billingProviderComponents?.originals?.user) || // For 2020-lite, a separate modal is shown
    // TODO: never hardcode plan ids in code - replace with a feature check
    corePlan?.id === '2020-lite'
  const billingProviderStorageComponentsPresent =
    Boolean(billingProviderComponents?.originals?.storage) || // For 2020-lite, a separate modal is shown
    // TODO: never hardcode plan ids in code - replace with a feature check
    corePlan?.id === '2020-lite'
  const isArtist = useFeatureEnabled(IS_ARTIST)
  const isBankDraftSubscriber =
    user.business?.private_metadata?.plan_type === 'bank_draft_subscriber'

  // const subscription = useSelector(state => state.subscription)
  const period = useSelector(getSubscriptionPeriod)

  const noComponentPurchaseDueTo = useSelector(
    getSelfServeBillingPermissions,
  ).no_seats_and_tracks_due_to
  useEffect(() => {
    if (
      !subscription.loading &&
      !subscription.loaded &&
      !isBankDraftSubscriber
    ) {
      dispatch(loadBusinessSubscription())
    }

    if (!businessUsers.loading && !businessUsers.loaded) {
      dispatch(loadBusinessUsers())
    }
  }, [])

  const isAdmin = user.permissions?.includes('can_change_business_settings')

  return (
    <SubscribedUserBilling
      planName={user?.business?.private_metadata?.plan?.name}
      isArtist={isArtist}
      isAdmin={isAdmin}
      usersLimits={usersLimits}
      tracksLimits={tracksLimits}
      disabledUpdatePaymentDetails={
        !isAdmin || !subscription.chargebee?.subscription_id
      }
      showUpdatePaymentDetails={!!subscription.chargebee?.subscription_id}
      paymentOverdue={
        user?.business?.private_metadata.payment_overdue ||
        user?.business?.private_metadata.payment_overdue_bank_wire
      }
      subscription={subscription}
      bankDraftSubscriber={isBankDraftSubscriber}
      noComponentPurchaseDueTo={noComponentPurchaseDueTo}
      period={period}
      lastUserAddedDate={user?.business?.last_user_added?.date}
      enableChangeSeatsModal={
        !noComponentPurchaseDueTo && billingProviderSeatsComponentsPresent
      }
      enableChangeTracksModal={
        !noComponentPurchaseDueTo && billingProviderStorageComponentsPresent
      }
      loading={subscription.loading || businessUsers.loading}
      billingProviderComponentsLoading={
        billingProviderComponents.loading ||
        usersLimits.updating ||
        tracksLimits.updating
      }
    />
  )
}

export default SubscribedUserBillingContainer
